/**
 * RunCompare — side-by-side run comparison view (P11b — AC-P11.4).
 *
 * Routed at /runs/compare?a=<run_a_id>&b=<run_b_id>. Calls the
 * /api/v1/pipeline-runs/compare API and renders per-run cards,
 * computed deltas, per-stage diffs, and the schema-diff section
 * (added/removed/rename/type-change) when both runs carry
 * `config.source_schema`.
 */

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  pipelineRuns,
  type PipelineRunSummary,
  type RunCompareResponse,
  type SchemaDiffSection,
  type StageDiffEntry,
} from "../../../apiv1";

interface Props {
  onBack?: () => void;
}

function fmtDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return "—";
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

function fmtTimestamp(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function DeltaPill({
  label,
  value,
  invert = false,
}: {
  label: string;
  value: number | null;
  invert?: boolean;
}) {
  if (value === null) {
    return (
      <div className="px-3 py-2 bg-white/5 rounded border border-white/10">
        <div className="text-[11px] uppercase tracking-wide text-white/40">
          {label}
        </div>
        <div className="text-sm text-white/40 font-mono">—</div>
      </div>
    );
  }
  const positive = value > 0;
  const negative = value < 0;
  // Good if delta moves in the "right" direction. For failed-count we want
  // negative-as-good; for succeeded-count we want positive-as-good. Caller
  // passes invert=true for "down is good" metrics.
  const good = invert ? negative : positive;
  const bad = invert ? positive : negative;
  const color =
    value === 0
      ? "text-white/60"
      : good
        ? "text-emerald-400"
        : bad
          ? "text-red-400"
          : "text-white/60";
  return (
    <div
      className="px-3 py-2 bg-white/5 rounded border border-white/10"
      data-testid={`delta-${label.toLowerCase()}`}
    >
      <div className="text-[11px] uppercase tracking-wide text-white/40">
        {label}
      </div>
      <div className={`text-sm font-mono ${color}`}>
        {positive ? "+" : ""}
        {value}
      </div>
    </div>
  );
}

function RunCard({ label, run }: { label: string; run: PipelineRunSummary }) {
  const statusColor =
    run.status === "completed"
      ? "text-emerald-400"
      : run.status === "failed"
        ? "text-red-400"
        : "text-amber-300";
  return (
    <div
      className="bg-white/5 border border-white/10 rounded p-4"
      data-testid={`run-card-${label.toLowerCase()}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-white/40">
            Run {label}
          </div>
          <div className="font-mono text-xs text-white/60">{run.id}</div>
        </div>
        {run.is_pinned && (
          <span
            className="px-2 py-0.5 text-[11px] rounded bg-amber-400/10 text-amber-300 border border-amber-400/30"
            data-testid={`pin-badge-${label.toLowerCase()}`}
          >
            📌 {run.pin_label ?? "pinned"}
          </span>
        )}
      </div>
      <div className="text-sm font-medium mb-2">{run.name}</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div className="text-white/40">status</div>
        <div className={`${statusColor} font-mono`}>{run.status}</div>
        <div className="text-white/40">started</div>
        <div className="text-white/70 font-mono text-[11px]">
          {fmtTimestamp(run.started_at)}
        </div>
        <div className="text-white/40">duration</div>
        <div className="text-white/70 font-mono">
          {fmtDuration(run.duration_seconds)}
        </div>
        <div className="text-white/40">succeeded</div>
        <div className="text-emerald-400 font-mono">{run.succeeded_count}</div>
        <div className="text-white/40">failed</div>
        <div className="text-red-400 font-mono">{run.failed_count}</div>
        <div className="text-white/40">skipped</div>
        <div className="text-white/60 font-mono">{run.skipped_count}</div>
      </div>
    </div>
  );
}

function StageDiffsTable({ stages }: { stages: StageDiffEntry[] }) {
  if (stages.length === 0) {
    return (
      <div className="text-white/40 text-xs">
        No stage-level differences between these runs.
      </div>
    );
  }
  return (
    <table className="w-full text-xs" data-testid="stage-diffs-table">
      <thead>
        <tr className="text-left text-white/40 border-b border-white/10">
          <th className="py-2 pr-4">Stage</th>
          <th className="py-2 pr-4">Before</th>
          <th className="py-2 pr-4">After</th>
        </tr>
      </thead>
      <tbody>
        {stages.map((s) => (
          <tr
            key={s.stage_name}
            className="border-b border-white/5"
            data-testid={`stage-diff-${s.stage_name}`}
          >
            <td className="py-2 pr-4 font-mono">{s.stage_name}</td>
            <td className="py-2 pr-4 font-mono text-[11px] text-white/60">
              {s.before ? JSON.stringify(s.before) : "(missing)"}
            </td>
            <td className="py-2 pr-4 font-mono text-[11px] text-white/60">
              {s.after ? JSON.stringify(s.after) : "(missing)"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SchemaDiffPanel({ section }: { section: SchemaDiffSection }) {
  if (!section.available) {
    return (
      <div
        className="text-white/40 text-xs italic"
        data-testid="schema-diff-unavailable"
      >
        Schema-diff unavailable: {section.reason ?? "no source schema captured"}
      </div>
    );
  }
  const diff = section.diff!;
  const totalChanges =
    (diff.summary.tables_added ?? 0) +
    (diff.summary.tables_removed ?? 0) +
    (diff.summary.columns_added ?? 0) +
    (diff.summary.columns_removed ?? 0) +
    (diff.summary.type_changes ?? 0) +
    (diff.summary.rename_candidates ?? 0);
  if (totalChanges === 0) {
    return (
      <div className="text-emerald-400 text-xs" data-testid="schema-diff-clean">
        ✓ No schema changes detected.
      </div>
    );
  }
  return (
    <div data-testid="schema-diff-panel">
      <div className="flex gap-2 flex-wrap mb-3">
        {Object.entries(diff.summary)
          .filter(([, v]) => v > 0)
          .map(([k, v]) => (
            <span
              key={k}
              className="px-2 py-0.5 text-[11px] bg-white/5 border border-white/10 rounded font-mono"
            >
              {k.replace(/_/g, " ")}: {v}
            </span>
          ))}
      </div>
      {diff.table_diffs.map((td) => (
        <div
          key={td.table}
          className="mb-3 border border-white/10 rounded p-3"
          data-testid={`schema-diff-table-${td.table}`}
        >
          <div className="font-mono text-xs text-white/80 mb-2">{td.table}</div>
          {td.rename_candidates.length > 0 && (
            <div className="mb-2">
              <div className="text-[11px] uppercase text-amber-300/70 mb-1">
                Possible renames
              </div>
              <ul className="text-xs text-white/70 space-y-0.5">
                {td.rename_candidates.map((rc) => (
                  <li
                    key={`${rc.before_name}->${rc.after_name}`}
                    className="font-mono"
                  >
                    {rc.before_name} → {rc.after_name}
                    <span className="text-white/40">
                      {" "}
                      ({rc.data_type}, {(rc.confidence * 100).toFixed(0)}%)
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {td.added.length > 0 && (
            <div className="mb-2">
              <div className="text-[11px] uppercase text-emerald-400/70 mb-1">
                Added
              </div>
              <ul className="text-xs text-emerald-300/90 space-y-0.5">
                {td.added.map((c) => (
                  <li key={c.column.name} className="font-mono">
                    + {c.column.name} ({c.column.data_type}
                    {c.column.nullable ? ", null" : ""})
                  </li>
                ))}
              </ul>
            </div>
          )}
          {td.removed.length > 0 && (
            <div className="mb-2">
              <div className="text-[11px] uppercase text-red-400/70 mb-1">
                Removed
              </div>
              <ul className="text-xs text-red-300/90 space-y-0.5">
                {td.removed.map((c) => (
                  <li key={c.column.name} className="font-mono">
                    − {c.column.name} ({c.column.data_type})
                  </li>
                ))}
              </ul>
            </div>
          )}
          {td.type_changes.length > 0 && (
            <div>
              <div className="text-[11px] uppercase text-amber-300/70 mb-1">
                Type changes
              </div>
              <ul className="text-xs text-amber-200/90 space-y-0.5">
                {td.type_changes.map((tc) => (
                  <li key={tc.column} className="font-mono">
                    ~ {tc.column}: {tc.before_type}
                    {tc.before_nullable ? "?" : ""} → {tc.after_type}
                    {tc.after_nullable ? "?" : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function RunCompare({ onBack }: Props) {
  const [params] = useSearchParams();
  const a = params.get("a");
  const b = params.get("b");

  const [data, setData] = useState<RunCompareResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!a || !b) {
      setError("Both ?a and ?b query parameters are required.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    pipelineRuns
      .compare(a, b)
      .then((d) => setData(d))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [a, b]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Run comparison</h1>
          <p className="text-sm text-white/50 mt-1">
            Side-by-side diff between two pipeline runs (metadata, deltas, stage
            status, schema)
          </p>
        </div>
        {onBack && (
          <button
            onClick={onBack}
            className="px-3 py-1.5 text-xs bg-white/10 hover:bg-white/15 rounded"
          >
            Back
          </button>
        )}
      </div>

      {error && (
        <div
          className="mb-4 p-3 bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded"
          data-testid="compare-error"
        >
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-white/40 text-sm">Loading comparison…</div>
      ) : data ? (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <RunCard label="A" run={data.run_a} />
            <RunCard label="B" run={data.run_b} />
          </div>

          <section>
            <h2 className="text-sm font-semibold mb-3">Deltas (B − A)</h2>
            <div className="grid grid-cols-4 gap-2">
              <DeltaPill
                label="Succeeded"
                value={data.deltas.succeeded_delta}
              />
              <DeltaPill
                label="Failed"
                value={data.deltas.failed_delta}
                invert
              />
              <DeltaPill
                label="Skipped"
                value={data.deltas.skipped_delta}
                invert
              />
              <DeltaPill
                label="Duration"
                value={data.deltas.duration_delta_seconds}
                invert
              />
            </div>
          </section>

          <section>
            <h2 className="text-sm font-semibold mb-3">Stage diffs</h2>
            <StageDiffsTable stages={data.stage_diffs} />
          </section>

          <section>
            <h2 className="text-sm font-semibold mb-3">Schema diff</h2>
            <SchemaDiffPanel section={data.schema_diff} />
          </section>
        </div>
      ) : null}
    </div>
  );
}
